/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import { AlwaysLightMode, Chip, Grid, TabContext, Typography2 } from '@coursera/cds-core';
import type { PageConfigComponent as PageConfigComponentTrackingData } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import type { Filter } from 'bundles/cds-labs/components/Modules/CollectionsModule/types';
import Image from 'bundles/cds-labs/components/Modules/components/Image';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/cds-labs';

const TrackedChip = withSingleTracked({ type: 'BUTTON' })(Chip);

const styles = {
  filters: (hasVerticalLayout: boolean) => css`
    margin-bottom: var(--cds-spacing-100);
    ${hasVerticalLayout ? 'margin-top: var(--cds-spacing-150);' : ''}

    && {
      gap: var(--cds-spacing-100);
    }
  `,

  chip: css`
    &.cds-Chip-root {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-neutral-background-primary);
      border-color: var(--cds-color-neutral-disabled-strong);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-blue-800);
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-blue-800);
    }

    &.cds-Chip-root:hover {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-neutral-disabled-weak);
      border-color: var(--cds-color-neutral-primary);
    }
  `,

  imageFilterContainer: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    white-space: nowrap;
  `,
};

type Props = {
  filters: Filter[];
  currentCollectionSetIndex: number;
  isSideBySideLayout: boolean;
  shouldRenderImagesForFilters: boolean;
  customChipStyles?: SerializedStyles;
  onFilterChange?: (index: number, filterName: string) => void;
  collections?: CarouselCollection[];
  eventingData?: {
    pageConfigComponent?: PageConfigComponentTrackingData;
  };
};

const CollectionFilterChips = ({
  filters,
  currentCollectionSetIndex,
  isSideBySideLayout,
  shouldRenderImagesForFilters,
  customChipStyles,
  onFilterChange,
  collections,
  eventingData,
}: Props) => {
  const trackV3 = useTracker();

  return (
    <TabContext value={currentCollectionSetIndex.toString()}>
      <Grid
        container
        aria-label={_t('Filter By')}
        css={styles.filters(!isSideBySideLayout)}
        role="tablist"
        wrap="wrap"
        direction="row"
      >
        {filters.map(({ filterName, id, filterImage, eventingData: filterEventingData }, idx) => {
          let tabContents: React.ReactText;
          if (shouldRenderImagesForFilters) {
            // @ts-expect-error Chip only accepts ReactText, but this JSX works as a child.
            tabContents = (
              <div css={styles.imageFilterContainer}>
                <Image {...filterImage} description="" width={24} height={24} />
                <Typography2 variant="subtitleSmall" component="span">
                  {filterName}
                </Typography2>
              </div>
            );
          } else {
            tabContents = filterName ?? '';
          }

          const handleClick = () => {
            trackV3('click_button', {
              carousel: {
                id,
                name: filterName,
                recommender: collections?.[currentCollectionSetIndex]?.recommender,
                ...(filterEventingData?.carouselFilter ? { filter: filterEventingData.carouselFilter } : {}),
              },
              button: {
                name: 'collections_filter_chip',
                ...(eventingData?.pageConfigComponent ? { pageConfigComponent: eventingData.pageConfigComponent } : {}),
              },
            });
            onFilterChange?.(idx, filterName);
          };

          const chip = (
            <TrackedChip
              key={id}
              selected={currentCollectionSetIndex === idx}
              aria-selected={currentCollectionSetIndex === idx}
              aria-checked={undefined}
              onClick={handleClick}
              css={shouldRenderImagesForFilters ? styles.chip : customChipStyles}
              aria-label={filterName}
              role="tab"
              aria-controls="collections-results"
              trackingName="collections_filter_chip"
            >
              {tabContents}
            </TrackedChip>
          );

          if (shouldRenderImagesForFilters) {
            return <AlwaysLightMode key={id}>{chip}</AlwaysLightMode>;
          }

          return chip;
        })}
      </Grid>
    </TabContext>
  );
};

export default CollectionFilterChips;
